// useLeadAgentTierPricing.hook.ts — shared price lookup for a single Lead
// Agent tier (RET-1230 / C2). Used by the banner's reco block (Unit 4 TODO)
// and as the lightweight price the modal also reuses. The banner NEVER blocks
// on billing: a missing catalog simply yields a null price (no price shown).

import { useEffect, useState } from 'react';
import { SubscriptionManagerService } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.service';
import { ePlanFrequency } from 'src/providers/SubscriptionProvider/Subscription.enums';
import {
  findLeadAgentProductForTier,
  formatTierPrice,
} from './modals/acceptanceModals.functions';
import type { tLeadAgentSelectableTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';

export type tUseLeadAgentTierPricingArgs = {
  workspaceId: string;
  tier: tLeadAgentSelectableTier;
  frequency: ePlanFrequency;
  currency: string;
  /** Skip the fetch entirely (e.g. banner not visible) to avoid noise. */
  enabled: boolean;
};

/**
 * Resolves the formatted catalog price ("$29.00/mes") for a single tier. Best
 * effort: any failure or missing SKU returns null, and the consumer shows no
 * price. Fetches lazily and only when `enabled`.
 */
export function useLeadAgentTierPricing(args: tUseLeadAgentTierPricingArgs): {
  price: string | null;
} {
  const { workspaceId, tier, frequency, currency, enabled } = args;
  const [price, setPrice] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !workspaceId || !currency) {
      setPrice(null);
      return;
    }

    let cancelled = false;
    SubscriptionManagerService.getProducts(workspaceId)
      .then((response) => {
        if (cancelled) return;
        const products = [
          ...response.allRelatedProducts,
          ...response.availableForPurchase,
        ];
        const product = findLeadAgentProductForTier(tier, products, {
          frequency,
          currency,
        });
        setPrice(product ? formatTierPrice(product, frequency) : null);
      })
      .catch(() => {
        if (cancelled) return;
        setPrice(null);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, workspaceId, tier, frequency, currency]);

  return { price };
}
